<template>
  <div
    class="ad-carousel"
    :style="carouselStyle"
    @mouseenter="pauseScrolling"
    @mouseleave="resumeScrolling"
  >
    <ul class="carousel-wrapper" ref="carouselWrapper">
      <li v-for="(item, index) in extendedDataList" :key="`${item.uid}-${index}`" class="carousel-item">
        <img
          v-if="item.photoList"
          :src="item.photoList[0]"
          :alt="item.subjectName"
          class="carousel-image"
          @click="goLink(item)"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import { getPartnerList, addLinkCount } from "../../api/partner";

export default {
  name: "PartnerList",
  props: {
    height: {
      type: String,
      default: "200px"
    },
    speed: {
      type: String,
      default: "50" // pixels per second
    },
    autoStart: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      offset: 0,
      wrapperWidth: 0,
      animationFrame: null,
      dataList: [],  // 合作伙伴列表
      isComponentActive: true, // 标记组件是否活跃
    };
  },
  computed: {
    carouselStyle() {
      return {
        height: this.height,
        overflow: "hidden",
        position: "relative",
        width: "100%"
      };
    },
    extendedDataList() {
      // 如果数据为空，返回空数组
      if (!this.dataList || this.dataList.length === 0) {
        return [];
      }
      // Duplicate dataList to ensure continuous scrolling
      return [...this.dataList, ...this.dataList];
    },
    // 计算实际滚动速度（像素/帧）
    stepSize() {
      return parseFloat(this.speed) / 60;
    }
  },
  watch: {
    dataList: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.updateWrapperWidth();
          });
        }
      },
      deep: true
    }
  },
  created() {
    this.partnerList();
  },
  mounted() {
    // 监听窗口大小变化，重新计算宽度
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    /**
     * 清理所有资源和事件监听
     */
    cleanup() {
      this.isComponentActive = false;
      this.stopScrolling();
      window.removeEventListener('resize', this.handleResize);

      // 清空数据，释放内存
      this.dataList = null;
      this.animationFrame = null;
    },

    /**
     * 更新轮播包装器宽度
     */
    updateWrapperWidth() {
      if (!this.$refs.carouselWrapper) return;

      const scrollWidth = this.$refs.carouselWrapper.scrollWidth;
      this.wrapperWidth = scrollWidth / 2; // Half because we doubled the dataList

      // 重置偏移量，避免显示异常
      if (this.wrapperWidth > 0) {
        this.startScrolling();
      }
    },

    /**
     * 处理窗口大小变化
     */
    handleResize() {
      if (!this.isComponentActive) return;

      // 防抖处理
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.isComponentActive && this.$refs.carouselWrapper) {
          const wasScrolling = !!this.animationFrame;
          if (wasScrolling) {
            this.stopScrolling();
          }
          this.updateWrapperWidth();
          if (wasScrolling && this.autoStart) {
            this.startScrolling();
          }
        }
      }, 150);
    },

    /**
     * 开始滚动动画
     */
    startScrolling() {
      // 如果已经存在动画，先停止
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      // 检查是否有足够的内容可以滚动
      if (!this.wrapperWidth || this.wrapperWidth <= 0 || !this.extendedDataList.length) {
        return;
      }

      const step = () => {
        // 检查组件是否还活跃
        if (!this.isComponentActive || !this.$refs.carouselWrapper) {
          return;
        }

        this.offset += this.stepSize;

        // 无缝滚动：当偏移量超过一半宽度时重置
        if (this.offset >= this.wrapperWidth) {
          this.offset = 0;
        }

        // 应用变换
        if (this.$refs.carouselWrapper) {
          this.$refs.carouselWrapper.style.transform = `translateX(${-this.offset}px)`;
        }

        // 继续动画
        this.animationFrame = requestAnimationFrame(step);
      };

      step();
    },

    /**
     * 停止滚动动画
     */
    stopScrolling() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    },

    /**
     * 暂停滚动（鼠标悬停时使用）
     */
    pauseScrolling() {
      this.stopScrolling();
    },

    /**
     * 恢复滚动
     */
    resumeScrolling() {
      if (this.autoStart && this.wrapperWidth > 0) {
        this.startScrolling();
      }
    },

    /**
     * 获取合作伙伴列表
     */
    partnerList() {
      let params = {};
      params.currentPage = 1;
      params.pageSize = 100;

      getPartnerList(params).then(response => {
        // 检查组件是否还存活
        if (!this.isComponentActive) return;

        if (response.code == this.$ECode.SUCCESS) {
          this.dataList = response.data.records || [];
        }
      }).catch(error => {
        console.error('获取合作伙伴列表失败:', error);
        if (this.isComponentActive) {
          this.$message && this.$message.error('加载合作伙伴失败');
        }
      });
    },

    /**
     * 跳转链接
     */
    goLink: function(item) {
      if (!item || !item.url) {
        console.warn('无效的链接项');
        return;
      }

      let params = new URLSearchParams();
      params.append("uid", item.uid);

      addLinkCount(params).then(response => {
        if (response.code == this.$ECode.SUCCESS) {
          this.openLink(item.url);
        }
      }).catch(error => {
        console.error('记录链接点击失败:', error);
        // 即使记录失败，也打开链接
        this.openLink(item.url);
      });
    },

    /**
     * 打开链接（兼容移动端和PC端）
     */
    openLink(url) {
      if (!url) return;

      const userAgent = navigator.userAgent;
      // android端
      const isAndroid = /Android|Adr/i.test(userAgent);
      // ios端
      const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

      if (isIOS) {
        // iOS 使用 location.href 避免被拦截
        window.location.href = url;
      } else {
        // Android 和 PC 使用 window.open
        window.open(url, "_blank");
      }
    }
  }
};
</script>

<style scoped>
.ad-carousel {
  width: 100%;
  position: relative;
}

.carousel-wrapper {
  display: flex;
  align-items: center;
  will-change: transform;
  margin: 0;
  padding: 0;
  list-style: none;
}

.carousel-item {
  flex: 0 0 auto;
  margin: 0 15px; /* Adds spacing between items */
  box-sizing: border-box; /* Ensure layout integrity */
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-image {
  width: 180px;
  height: 180px;
  cursor: pointer;
  object-fit: contain;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.carousel-image:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  transform: scale(1.05);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .carousel-item {
    margin: 0 5px; /* 减少间距以适应手机屏幕 */
  }

  .carousel-image {
    width: 80px; /* 调整图片大小以适应手机屏幕 */
  }
}

.ad-carousel:hover .carousel-wrapper {
  animation-play-state: paused;
}
</style>



// WEBPACK FOOTER //
// src/components/PartnerList/index.vue